import axios from "axios";
import type { FormData, FormType } from "../types";
import { deserializeFile, downscaleImage, type SerializedFile } from "./imageUtils";
import { t } from "../locales/nl";
import { env } from "../config/env";
import { logNetworkError } from "./errorUtils";

// Create axios instance with base URL
const api = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 120000, // 2 minutes for large multipart uploads
});

// Error types for better categorization
export interface NetworkError {
  type: "network" | "server" | "client" | "timeout" | "rate-limit" | "unknown";
  message: string;
  statusCode?: number;
  originalError?: any;
}

api.interceptors.request.use((config) => {
  if (config.data instanceof globalThis.FormData && config.headers) {
    delete config.headers["Content-Type"];
  }
  return config;
});

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429) {
      // Rate limit exceeded
      const retryAfter = error.response.headers["retry-after"];
      const waitTime = retryAfter ? parseInt(retryAfter, 10) : 60;
      return Promise.reject(
        new Error(t.errors.rateLimit.replace("{seconds}", waitTime.toString())),
      );
    }
    return Promise.reject(error);
  },
);

const getEndpointForFormType = (formType: FormType): string => {
  switch (formType) {
    case "ophaal":
      return "form/ophaal";
    case "aflever":
      return "form/aflever";
    case "inname":
      return "form/inname";
    case "omruil":
      return "form/omruil";
    default:
      throw new Error(`Unknown form type: ${formType}`);
  }
};

const prepareMultipartFormData = async (
  formData: FormData,
): Promise<globalThis.FormData> => {
  const multipartData = new globalThis.FormData();

  const formFields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(formData)) {
    if (key !== "photos") {
      formFields[key] = value;
    }
  }
  multipartData.append("data", JSON.stringify(formFields));

  if (!formData.photos) {
    return multipartData;
  }

  const photoEntries = Object.entries(formData.photos);

  try {
    for (const [key, files] of photoEntries) {
      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const file = files[fileIndex];
        let fileObj: File;
        const isNativeFile = file instanceof File;
        if (!isNativeFile) {
          fileObj = await deserializeFile(file as SerializedFile);
        } else {
          fileObj = file;
        }

        // Safety net: only downscale files from old drafts (deserialized).
        // Native File objects were already downscaled in PhotoUpload.
        if (!isNativeFile) {
          try {
            fileObj = await downscaleImage(fileObj);
          } catch (downscaleError) {
            console.warn("Image downscale failed, uploading original file:", downscaleError);
          }
        }

        multipartData.append(
          `photos[${key}][${fileIndex}]`,
          fileObj,
          fileObj.name,
        );
      }
    }
  } catch (error) {
    console.error("Error processing images:", error);
    logNetworkError(
      {
        type: "client",
        message:
          "Er is een fout opgetreden bij het verwerken van de afbeeldingen. Probeer het opnieuw.",
        originalError: error,
      },
      {
        formType: "image-processing",
        additionalData: { error: "Image processing failed" },
      },
    );
    throw new Error(
      "Er is een fout opgetreden bij het verwerken van de afbeeldingen. Probeer het opnieuw.",
    );
  }

  return multipartData;
};

export const submitVehicleForm = async (
  formData: FormData,
  formType: FormType,
  onProgress?: (_progress: number) => void,
) => {
  try {
    const endpoint = getEndpointForFormType(formType);
    onProgress?.(10);

    const multipartData = await prepareMultipartFormData(formData);
    onProgress?.(30);

    const response = await api.post(endpoint, multipartData, {
      timeout: 600000, // 10 minutes for large multipart uploads (matches backend functionTimeout)
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const uploadProgress = progressEvent.loaded / progressEvent.total;
          const totalProgress = 30 + uploadProgress * 70;
          onProgress?.(totalProgress);
        }
      },
    });
    return response.data;
  } catch (error) {
    // Categorize the error type
    let errorType: NetworkError["type"] = "unknown";
    let statusCode: number | undefined;
    let errorMessage = t.errors.generic;

    if (axios.isAxiosError(error)) {
      statusCode = error.response?.status;

      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        errorType = "timeout";
        errorMessage = t.errors.timeout;
      } else if (
        error.code === "ERR_NETWORK" ||
        error.message.includes("Network Error")
      ) {
        errorType = "network";
        errorMessage = t.errors.network;
      } else if (statusCode) {
        if (statusCode >= 500) {
          errorType = "server";
          errorMessage = t.errors.server;
        } else if (statusCode === 429) {
          errorType = "rate-limit";
          errorMessage = error.message; // Use the message from our interceptor
        } else if (statusCode >= 400) {
          errorType = "client";
          errorMessage = error.response?.data?.message || t.errors.client;
        }
      }
    }

    // Create structured error
    const networkError: NetworkError = {
      type: errorType,
      message: errorMessage,
      statusCode,
      originalError: error,
    };

    // Enhanced error logging with context
    logNetworkError(networkError, {
      formType,
      endpoint: getEndpointForFormType(formType),
      additionalData: {
        formDataWithoutImages: {
          ...formData,
          photos: Object.keys(formData.photos).reduce(
            (acc, key) => {
              acc[key] = formData.photos[key]
                ? `${formData.photos[key].length} files`
                : "no files";
              return acc;
            },
            {} as Record<string, string>,
          ),
        },
      },
    });

    throw networkError;
  }
};
